/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { createEnum, EnumSet } from '@gh/core-data';
import { ContactDetailsCommunicationDataDto } from './contact-details-communication.dto';
import { ContactDetailsInvitationSubTabDto } from './contact-details-customer.dto';
import {
  ContactDeliveryMethod,
  ContactDetailsCustomFieldListDto,
  ContactDetailsPaymentCreditorDto,
  ContactDetailsTermsPayeeDto,
} from './contact-details-shared.dto';

export const CreditorType = createEnum('CREDITOR_TYPE', {
  Supplier: 'SUPPLIER',
  SubContractor: 'SUB_CONTRACTOR',
});

export type CreditorType = EnumSet<typeof CreditorType>;

export const EmailMatch = createEnum('EMAIL_MATCH', {
  Same: 'SAME',
  Different: 'DIFFERENT',
});

export type EmailMatch = EnumSet<typeof EmailMatch>;

export const BusinessNumberType = createEnum('BUSINESS_NUMBER_TYPE', {
  Tax: 'TAX_EIN',
  SecurityNo: 'SECURITY_NR',
});

export type BusinessNumberType = EnumSet<typeof BusinessNumberType>;

export const WithholdingTaxType = createEnum('WHT_TAX', {
  NotWhtLiable: 'NOT_WHT_LIABLE',
  WhtPayer: 'WHT_PAYER',
  WhtExemption: 'WHT_EXEMPTION',
});

export type WithholdingTaxType = EnumSet<typeof WithholdingTaxType>;

export const TrackingCaption = createEnum('CAPTION', {
  Caption1: 'CAPTION_1',
  Caption2: 'CAPTION_2',
  Caption3: 'CAPTION_3',
  Caption4: 'CAPTION_4',
  Caption5: 'CAPTION_5',
  Caption6: 'CAPTION_6',
  Caption7: 'CAPTION_7',
  Caption8: 'CAPTION_8',
  Caption9: 'CAPTION_9',
  Caption10: 'CAPTION_10',
});

export type TrackingCaption = EnumSet<typeof TrackingCaption>;

export const SupplierServiceType = createEnum('SUPPLIER_SERVICE_TYPE', {
  Supply: 1,
  Install: 2,
  SupplyAndInstall: 3,
});

export type SupplierServiceType = EnumSet<typeof SupplierServiceType>;

export const SupplierCategoryType = createEnum('SUPPLIER_CATEGORY', {
  Hvac: 1,
  Appliances: 2,
  BrickBlockWork: 3,
  Cabinetry: 4,
  Carpenter: 5,
  Siding: 6,
  Concreting: 7,
  Countertops: 8,
  Earthworks: 9,
  Electrical: 10,
  Fencing: 11,
  Flooring: 12,
  GarageDoors: 13,
  Drywall: 14,
  Lansdscaping: 15,
  Painting: 16,
  Plumbing: 17,
  Pools: 18,
  ProfessionalServices: 19,
  Stucco: 20,
  Roofing: 21,
  SedimentControl: 22,
  Solar: 23,
  Stairs: 24,
  Masonry: 25,
  TemporaryHire: 26,
  PestProtection: 27,
  TimberHardware: 28,
  Waterproofing: 29,
  WindowsScreens: 30,
  WindowCoverings: 31,
  Retail: 32,
});

export type SupplierCategoryType = EnumSet<typeof SupplierCategoryType>;

export type ContactDetailsCreditorDeliveryInfoDto = {
  deliveryMethod: ContactDeliveryMethod;
  emailMatch: EmailMatch;
  email?: string;
  address?: string;
};

export type ContactDetailsCreditorCommonDto = {
  type: CreditorType;
  defaultDeliveryToEmail: boolean;
  orderBy: ContactDetailsCreditorDeliveryInfoDto;
  billingBy: ContactDetailsCreditorDeliveryInfoDto;
  scheduleBy: ContactDetailsCreditorDeliveryInfoDto;
  includeExtraDocs: boolean;
  provides: SupplierServiceType;
  category: SupplierCategoryType;
  businessInfo: ContactDetailsCreditorBusinessInfoDto;
};

export type ContactDetailsCreditorContractDto = {
  contractStart?: Date;
  contractEnd?: Date;
  contractSent?: Date;
  contractReceived?: Date;
};

export type ContactDetailsCreditorInsuranceDto = {
  company: string;
  policy: string;
  expired: Date;
};

export type ContactDetailsCreditorLiabilityDto = {
  company: string;
  policy: string;
  expired: Date;
};

export type ContactDetailsCreditorBusinessInfoDto = {
  // us region
  numberType?: BusinessNumberType;
  contractLicenseApplicable?: boolean;
  taxNumber?: string;
  socialNumber?: string;
  eligibleFor1099Form?: boolean;
  form1099name?: string;
  licenseNumber?: string;
  licenceExpired?: Date;
  // au region
  abn?: string;
  // au & nz region
  activity?: string;
  // nz region
  type?: WithholdingTaxType;
  gst?: string;
  defWhtRate?: number;
  taxRate?: number;
  certificateNbr?: string;
  certificateDate?: Date;
};

export type ContactDetailsCreditorTrackingDto = {
  value: TrackingCaption;
  name: string;
  selected: boolean;
};

export type ContactDetailsCreditorSubContractorDto = {
  contract: ContactDetailsCreditorContractDto;
  insurance: ContactDetailsCreditorInsuranceDto;
  liability: ContactDetailsCreditorLiabilityDto;
  tracking: ContactDetailsCreditorTrackingDto[];
};

export type ContactDetailsCreditorDto = {
  common: ContactDetailsCreditorCommonDto;
  subContractor: ContactDetailsCreditorSubContractorDto;
  payment: ContactDetailsPaymentCreditorDto;
  terms: ContactDetailsTermsPayeeDto;
  communication: ContactDetailsCommunicationDataDto;
  customFields: ContactDetailsCustomFieldListDto;
  invitations: ContactDetailsInvitationSubTabDto[];
};

export function isDeliveryMethodEmail(method: ContactDeliveryMethod): boolean {
  return method === ContactDeliveryMethod.Email;
}

export function isDeliveryMethodMail(method: ContactDeliveryMethod): boolean {
  return method === ContactDeliveryMethod.Mail;
}

export function isNotWhtLiable(type: WithholdingTaxType): boolean {
  return type === WithholdingTaxType.NotWhtLiable;
}

export function isWhtLiable(type: WithholdingTaxType): boolean {
  return type !== WithholdingTaxType.NotWhtLiable;
}

export function isWhtPayer(type: WithholdingTaxType): boolean {
  return type === WithholdingTaxType.WhtPayer;
}

export function isWhtExemption(type: WithholdingTaxType): boolean {
  return type === WithholdingTaxType.WhtExemption;
}
